import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { EventDiscussionPanel } from '@/components/events/EventDiscussionPanel';
import { MassCountdown } from '@/components/events/MassCountdown';
import { PartyTabs } from '@/components/events/PartyTabs';
import { SlotCard } from '@/components/events/SlotCard';
import { AppShell } from '@/components/layout/AppShell';
import { Badge } from '@/components/shared/Badge';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { DataStatePanel } from '@/components/shared/DataStatePanel';
import { EmptyState } from '@/components/shared/EmptyState';
import { MassLifecycleBadge } from '@/components/shared/MassLifecycleBadge';
import { PageHeader } from '@/components/shared/PageHeader';
import { Panel } from '@/components/shared/Panel';
import { StatCard } from '@/components/shared/StatCard';
import { getMassLifecycleSummary } from '@/features/events/selectors';
import { useAuth } from '@/hooks/useAuth';
import { useGuildData } from '@/hooks/useGuildData';
import type { EventDetailView, EventPartyView, SignupParticipant } from '@/types/domain';
import { formatDateTime, getLocalTimeZoneLabel } from '@/utils/date';

interface PendingRemovalState {
  participant: SignupParticipant;
  sourceLabel: string;
}

interface RosterDragState {
  participant: SignupParticipant;
  sourceSlotId: string;
}

function buildAlternativePartyLabels(parties: EventPartyView[], partyId: string): string[] {
  return parties
    .filter((party) => party.party.id !== partyId && party.summary.remainingCount > 0)
    .map((party) => party.party.label);
}

export function MassDetailsPage(): JSX.Element {
  const { eventId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getEventDetailView, joinSlot, cancelSignup, staffMoveSignup, staffRemoveSignup, isLoading, error, refreshData } = useGuildData();
  const { hasAnyRole } = useAuth();
  const detail = getEventDetailView(eventId);
  const [activePartyId, setActivePartyId] = useState(detail?.parties[0]?.party.id ?? '');
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemovalState | null>(null);
  const [dragState, setDragState] = useState<RosterDragState | null>(null);
  const [dragTargetSlotId, setDragTargetSlotId] = useState<string | undefined>();
  const [highlightedSlotId, setHighlightedSlotId] = useState<string | undefined>();
  const [pendingSlotFocusId, setPendingSlotFocusId] = useState<string | undefined>();
  const [isMoving, setIsMoving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const consumedSlotFocusIdRef = useRef<string | null>(null);
  const initialPartyId = detail?.parties[0]?.party.id ?? '';
  const canManageMasses = hasAnyRole(['admin', 'officer', 'mass_manager']);
  const timeZoneLabel = getLocalTimeZoneLabel();
  const focusSlotId = searchParams.get('slotId') ?? undefined;
  const focusMessageId = searchParams.get('messageId') ?? undefined;
  const focusResultId = searchParams.get('resultId') ?? undefined;

  const clearConsumedFocus = useCallback(
    (focusKey: 'slot' | 'discussion' | 'results', keys: string[]) => {
      const nextSearchParams = new URLSearchParams(searchParams);

      for (const key of keys) {
        nextSearchParams.delete(key);
      }

      if (nextSearchParams.get('focus') === focusKey) {
        nextSearchParams.delete('focus');
      }

      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    setActivePartyId(initialPartyId);
  }, [eventId, initialPartyId]);

  useEffect(() => {
    if (!focusSlotId) {
      consumedSlotFocusIdRef.current = null;
      setPendingSlotFocusId(undefined);
    }
  }, [focusSlotId]);

  useEffect(() => {
    if (!detail || !focusSlotId || consumedSlotFocusIdRef.current === focusSlotId) {
      return;
    }

    const matchingParty = detail.parties.find((party) => party.slots.some((slot) => slot.id === focusSlotId));

    if (matchingParty) {
      setActivePartyId(matchingParty.party.id);
      setPendingSlotFocusId(focusSlotId);
    }
  }, [detail, focusSlotId]);

  useEffect(() => {
    if (!pendingSlotFocusId) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      const element = document.getElementById(`slot-card-${pendingSlotFocusId}`);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedSlotId(pendingSlotFocusId);
        consumedSlotFocusIdRef.current = pendingSlotFocusId;
        setPendingSlotFocusId(undefined);
        clearConsumedFocus('slot', ['slotId', 'signupId']);
      }
    }, 150);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activePartyId, clearConsumedFocus, pendingSlotFocusId]);

  useEffect(() => {
    if (!highlightedSlotId) {
      return undefined;
    }

    const clearId = window.setTimeout(() => {
      setHighlightedSlotId((current) => (current === highlightedSlotId ? undefined : current));
    }, 2400);

    return () => {
      window.clearTimeout(clearId);
    };
  }, [highlightedSlotId]);

  const lifecycle = detail ? getMassLifecycleSummary(detail) : null;
  const selectedParty = detail?.parties.find((party) => party.party.id === activePartyId) ?? detail?.parties[0];
  const countdownState = lifecycle?.isFinishedLike ? 'finished' : lifecycle?.isStarted ? 'started' : 'scheduled';
  const canShowResultsAndDiscussion = Boolean(
    detail && lifecycle && (lifecycle.isStarted || (lifecycle.isFinishedLike && new Date(detail.startsAt).getTime() <= Date.now())),
  );
  const memberSignupLockedLabel =
    !canManageMasses && lifecycle && !lifecycle.isSignupAvailable
      ? lifecycle.isStarted || lifecycle.isFinishedLike
        ? 'Mass Started'
        : 'Signup Closed'
      : undefined;
  const memberSignupLockedHint =
    !canManageMasses && lifecycle && !lifecycle.isSignupAvailable
      ? lifecycle.isStarted || lifecycle.isFinishedLike
        ? 'Self-signup is locked because the mass is already underway or finished. Staff can still manage the roster.'
        : 'Self-signup is locked because signup is closed. Staff can still manage the roster.'
      : undefined;

  if (isLoading) {
    return (
      <AppShell>
        <DataStatePanel title="Loading mass details" description="Fetching parties, slots, signups, and waitlist data." />
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell>
        <DataStatePanel title="Unable to load this mass" description={error} actionLabel="Retry" onAction={() => void refreshData()} />
      </AppShell>
    );
  }

  if (!detail || !selectedParty || !lifecycle) {
    return (
      <AppShell>
        <EmptyState title="Mass not found" description="This event may have been archived or the link is invalid." />
      </AppShell>
    );
  }

  const allPartiesFull = detail.parties.every((party) => party.summary.totalCapacity > 0 && party.summary.isFull);

  const renderPartySection = (partyView: EventPartyView) => {
    const visibleSlots = canManageMasses ? partyView.slots : partyView.slots.filter((slot) => slot.isActive);
    const alternativePartyLabels = buildAlternativePartyLabels(detail.parties, partyView.party.id);

    return (
      <section key={partyView.party.id} className="mass-party-board">
        <div className="mass-party-board__header">
          <div>
            <strong>{partyView.party.label}</strong>
            <p>
              {partyView.summary.joinedCount} / {partyView.summary.totalCapacity} joined
              {partyView.summary.totalCapacity > 0 ? ` - ${partyView.summary.remainingCount} remaining` : ''}
            </p>
          </div>
          <div className="party-role-summary">
            {partyView.summary.roleDistribution.map((entry) => (
              <div key={entry.roleCategory} className="party-role-summary__item">
                <strong>{entry.roleCategory}</strong>
                <span>
                  {entry.joinedCount} / {entry.requiredCount}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="slot-grid">
          {visibleSlots.map((slot) => (
            <SlotCard
              key={slot.id}
              domId={`slot-card-${slot.id}`}
              isHighlighted={highlightedSlotId === slot.id}
              slot={slot}
              partyLabel={partyView.party.label}
              isPartyFull={partyView.summary.isFull}
              alternativePartyLabels={alternativePartyLabels}
              currentUserSignup={detail.currentUserSignup}
              canManageRoster={canManageMasses}
              draggedParticipant={dragState?.participant ?? null}
              draggedSourceSlotId={dragState?.sourceSlotId}
              isDropTarget={dragTargetSlotId === slot.id}
              signupLockedLabel={memberSignupLockedLabel}
              signupLockedHint={memberSignupLockedHint}
              onJoin={() => void joinSlot(detail.id, slot.id)}
              onRemove={() => void cancelSignup(detail.id)}
              onRemoveParticipant={(participant) =>
                setPendingRemoval({
                  participant,
                  sourceLabel: `${partyView.party.label}: ${slot.weaponName} (${slot.roleCategory})`,
                })
              }
              onParticipantDragStart={(participant, sourceSlotId) => {
                setDragState({
                  participant,
                  sourceSlotId,
                });
                setDragTargetSlotId(undefined);
              }}
              onParticipantDragEnd={() => {
                setDragState(null);
                setDragTargetSlotId(undefined);
              }}
              onParticipantDragTargetChange={setDragTargetSlotId}
              onParticipantDrop={(targetSlotId, payload) => {
                if (isMoving) {
                  return;
                }

                const signupId = dragState?.participant.signupId ?? payload?.signupId;

                if (!signupId) {
                  return;
                }

                setIsMoving(true);

                void staffMoveSignup(signupId, targetSlotId)
                  .finally(() => {
                    setIsMoving(false);
                    setDragState(null);
                    setDragTargetSlotId(undefined);
                  });
              }}
            />
          ))}
        </div>
      </section>
    );
  };

  return (
    <AppShell>
      <PageHeader
        title={detail.title}
        description={detail.description ? `${detail.description} ${timeZoneLabel}.` : timeZoneLabel}
        actions={
          <Link to="/masses" className="button button--secondary">
            Back to Masses
          </Link>
        }
      />

      <div className="stats-grid">
        <StatCard label="Local Start Time" value={formatDateTime(detail.startsAt)} hint={`Primary regroup target in ${timeZoneLabel}`} />
        <StatCard label="Local Signup Close" value={formatDateTime(detail.signupClosesAt)} hint={`Self-signup lock point in ${timeZoneLabel}`} />
        <StatCard label="Joined" value={detail.totalJoined} hint="Joined members on active slots" />
        <StatCard label="Required" value={detail.totalRequired} hint="Total capacity across active slots" />
        <StatCard label="Waitlist" value={detail.totalWaitlist} hint="Overflow queued automatically" />
      </div>

      <Panel
        title="Mass Status"
        description={
          lifecycle.isFinishedLike
            ? 'This mass is finished and now lives in results/discussion mode.'
            : lifecycle.isStarted
              ? 'The mass is underway. Self-signup is locked and staff control the live roster.'
              : 'Track signup readiness, countdown pressure, and roster status.'
        }
      >
        <div className="mass-status">
          <MassLifecycleBadge summary={lifecycle} />
          {detail.isFull && lifecycle.isSignupAvailable ? <Badge tone="warning">Mass Full</Badge> : null}
          {allPartiesFull && lifecycle.isSignupAvailable ? <Badge tone="danger">All Parties Full</Badge> : null}
          <MassCountdown startsAt={detail.startsAt} state={countdownState} />
          {detail.currentUserSignup ? (
            <p>
              You are currently {detail.currentUserSignup.status} on {detail.currentUserSignup.weaponNameSnapshot} as{' '}
              {detail.currentUserSignup.roleCategorySnapshot}.
            </p>
          ) : lifecycle.isSignupAvailable ? (
            <p>You do not have an active signup in this mass yet.</p>
          ) : (
            <p>This mass is no longer open for self-signup changes.</p>
          )}
        </div>
      </Panel>

      {canManageMasses ? (
        <Panel
          title="Staff Roster Board"
          description="Drag joined or waitlisted players between any active slots and parties, then use Kick only when someone needs to be removed entirely."
        >
          <div className="mass-party-board-stack">{detail.parties.map((partyView) => renderPartySection(partyView))}</div>
        </Panel>
      ) : (
        <>
          <PartyTabs
            parties={detail.parties.map((party) => ({
              id: party.party.id,
              label: party.party.label,
              joinedCount: party.summary.joinedCount,
              totalCapacity: party.summary.totalCapacity,
              remainingCount: party.summary.remainingCount,
              isFull: party.summary.isFull,
            }))}
            activePartyId={selectedParty.party.id}
            onChange={setActivePartyId}
          />

          <Panel
            title={`${selectedParty.party.label} Capacity`}
            description={
              selectedParty.summary.isFull
                ? `${selectedParty.party.label} is full. ${selectedParty.summary.joinedCount} / ${selectedParty.summary.totalCapacity} joined.`
                : `${selectedParty.summary.joinedCount} / ${selectedParty.summary.totalCapacity} joined. ${selectedParty.summary.remainingCount} remaining.`
            }
          >
            {renderPartySection(selectedParty)}
          </Panel>
        </>
      )}

      {canShowResultsAndDiscussion ? (
        <EventDiscussionPanel
          eventId={detail.id}
          title={detail.title}
          canUploadResults={canManageMasses}
          focusMessageId={focusMessageId}
          focusResultId={focusResultId}
          onMessageFocusConsumed={() => clearConsumedFocus('discussion', ['messageId'])}
          onResultFocusConsumed={() => clearConsumedFocus('results', ['resultId'])}
        />
      ) : null}

      <ConfirmationDialog
        open={Boolean(pendingRemoval)}
        title="Remove Signup"
        description={`Remove ${pendingRemoval?.participant.inGameName ?? 'this member'} from ${pendingRemoval?.sourceLabel ?? 'this mass'}? If they were joined and a waitlist exists, the first waitlist player will be promoted automatically.`}
        confirmLabel="Remove Signup"
        confirmVariant="danger"
        confirmationValue=""
        onConfirmationValueChange={() => undefined}
        isProcessing={isRemoving}
        onClose={() => {
          if (isRemoving) {
            return;
          }

          setPendingRemoval(null);
        }}
        onConfirm={() => {
          if (!pendingRemoval) {
            return;
          }

          setIsRemoving(true);

          void staffRemoveSignup(pendingRemoval.participant.signupId)
            .then(() => {
              setPendingRemoval(null);
            })
            .finally(() => {
              setIsRemoving(false);
            });
        }}
      />
    </AppShell>
  );
}
